
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { BookingDialog } from "@/components/BookingDialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { useCalendarConfig } from "./booking/CalendarConfig";
import { useEventHandlers } from "./booking/EventHandlers";
import { useBookingEvents } from "./booking/BookingEvents";
import { CalendarTabs } from "./booking/CalendarTabs";
import { ServiceCalendar } from "./booking/ServiceCalendar";
import { UnifiedBookingDialog } from "@/components/booking/UnifiedBookingDialog";
import { useProfileCheck } from "@/contexts/ProfileCheckContext";
import { ProfileCompletionAlert } from "./booking/ProfileCompletionAlert";
import { useProfileCompleteness } from "@/hooks/useProfileCompleteness";
import { useServicePermission } from "@/hooks/useServicePermission";

interface BookingSectionProps {
  service: any;
}

export const BookingSection = ({ service }: BookingSectionProps) => {
  const isMobile = useIsMobile();
  const { checkProfileBeforeBooking, redirectToProfile } = useProfileCheck();
  const { isComplete: isProfileComplete, isLoading: profileLoading, updateCompletionStatus } = useProfileCompleteness();
  const { isOwner, isAdmin } = useServicePermission(service?.id);
  
  const {
    getBusinessHoursConfig,
    getToolbarConfig,
    getTimeRange,
    getCalendarViews,
    getCalendarHeight,
    getInitialView
  } = useCalendarConfig({ service });

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState("");
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<string>(getInitialView());
  
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [isBookingDialogOpen, setIsBookingDialogOpen] = useState(false);

  // Get business hours for the service
  const businessHours = getBusinessHoursConfig();

  useEffect(() => {
    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();
      setIsLoggedIn(!!data.session);
      setCurrentUserId(data.session?.user.id || null);
    };
    
    checkAuth();
  }, []);

  const { events, fetchBookings } = useBookingEvents(service, currentUserId);

  const handleProfileCheckedDateClick = (info: any) => {
    if (isLoggedIn && !checkProfileBeforeBooking()) {
      redirectToProfile();
      return;
    }
    
    handleDateClick(info);
  };

  const { handleDateClick, handleEventClick } = useEventHandlers({
    isLoggedIn,
    setSelectedDate,
    setSelectedTimeSlot,
    setIsBookingOpen,
    setSelectedBooking,
    setBookingAction: () => {},
    setIsBookingActionOpen: () => {},
    currentUserId,
    setIsBookingDialogOpen,
    isServiceOwner: isOwner,
    isAdmin
  });

  const handleBookingClose = useCallback(() => {
    setIsBookingOpen(false);
    setSelectedTimeSlot("");
    fetchBookings();
  }, [fetchBookings]);
  
  const handleBookingDialogClose = useCallback(() => {
    setIsBookingDialogOpen(false);
    setSelectedBooking(null);
    fetchBookings();
  }, [fetchBookings]);

  const handleViewChange = useCallback((view: string) => {
    console.log('View change requested to:', view);
    setActiveView(view);
  }, []);

  const handleViewMount = useCallback((viewInfo: any) => {
    console.log('View mounted:', viewInfo.view.type);
    setActiveView(viewInfo.view.type);
  }, []);

  const calendarConfig = {
    toolbarConfig: getToolbarConfig(),
    businessHours: businessHours,
    timeRange: getTimeRange(),
    calendarViews: getCalendarViews(),
    calendarHeight: getCalendarHeight(),
    initialView: getInitialView()
  };

  // Profile completeness is now handled by useProfileCompleteness hook

  return (
    <div className="border-t border-gray-100 pt-2 mb-3">
      <h2 className="text-sm font-semibold mb-3">Book an Appointment</h2>
      <div className="bg-white rounded p-2">
        {isLoggedIn && !profileLoading && (
          <ProfileCompletionAlert 
            isComplete={isProfileComplete} 
          />
        )}
        
        <CalendarTabs 
          activeView={activeView} 
          onViewChange={handleViewChange} 
        />
        
        <ServiceCalendar
          events={events}
          handleDateClick={handleProfileCheckedDateClick}
          handleEventClick={handleEventClick}
          activeView={activeView}
          onViewMount={handleViewMount}
          calendarConfig={calendarConfig}
          isManagementView={false}
        />

        <div className="mt-2 text-xs text-gray-500">
          <p>Click on the calendar to select a time slot for booking. Click on your existing bookings to view, edit or cancel them.</p>
        </div>
      </div>

      {service.calendar && (
        <BookingDialog 
          serviceName={service.name} 
          serviceId={service.id}
          selectedDate={selectedDate}
          selectedTimeSlot={selectedTimeSlot}
          isOpen={isBookingOpen}
          onClose={handleBookingClose}
          operatingHours={businessHours}
        />
      )}
      
      <UnifiedBookingDialog
        isOpen={isBookingDialogOpen}
        onClose={handleBookingDialogClose}
        booking={selectedBooking}
        onSuccess={fetchBookings}
      />
    </div>
  );
};
